/**
 * Iconografia del shell.
 *
 * Dibujada a mano en SVG en vez de sumar una libreria de iconos: pesan menos que
 * la dependencia y garantizan que nada del set visual venga de otro producto.
 * Todos heredan `currentColor` y son `aria-hidden`: el significado lo aporta el
 * texto o el `aria-label` del control que los contiene.
 *
 * La segunda mitad del archivo son los iconos que los MODULOS piden por nombre
 * desde su `ui.py` (`MenuItem(icon="wallet")`). Con doce modulos instalados el
 * backend pide 33 nombres distintos; los que faltaban caian al icono de carpeta,
 * asi que trece de los catorce menus raiz se dibujaban identicos entre si. Todo
 * nombre que el backend pueda pedir tiene que estar en `ICONOS_DE_MENU`.
 */

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function Base({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export function IconMenu(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M4 7h16M4 12h16M4 17h10" />
    </Base>
  );
}

export function IconClose(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Base>
  );
}

export function IconSearch(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4 4" />
    </Base>
  );
}

export function IconChevronRight(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M9.5 5l7 7-7 7" />
    </Base>
  );
}

export function IconChevronDown(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M5 9l7 7 7-7" />
    </Base>
  );
}

export function IconChevronUpDown(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M8 9.5L12 5.5l4 4M8 14.5l4 4 4-4" />
    </Base>
  );
}

export function IconSun(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.2 5.2l1.4 1.4M17.4 17.4l1.4 1.4M18.8 5.2l-1.4 1.4M6.6 17.4l-1.4 1.4" />
    </Base>
  );
}

export function IconMoon(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M20 13.4A8.2 8.2 0 1110.6 4a6.6 6.6 0 009.4 9.4z" />
    </Base>
  );
}

export function IconMonitor(props: IconProps) {
  return (
    <Base {...props}>
      <rect x="3" y="4.5" width="18" height="12" rx="2" />
      <path d="M9 20h6M12 16.5V20" />
    </Base>
  );
}

export function IconBuilding(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M4 20h16" />
      <path d="M6 20V5.5A1.5 1.5 0 017.5 4h6A1.5 1.5 0 0115 5.5V20" />
      <path d="M15 20V10h2.5A1.5 1.5 0 0119 11.5V20" />
      <path d="M9 8h3M9 11.5h3M9 15h3" />
    </Base>
  );
}

export function IconHome(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M4 10.5L12 4l8 6.5V19a1 1 0 01-1 1h-4v-6H9v6H5a1 1 0 01-1-1z" />
    </Base>
  );
}

export function IconLogout(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M14 4h4a2 2 0 012 2v12a2 2 0 01-2 2h-4" />
      <path d="M10 8l-4 4 4 4M6 12h10" />
    </Base>
  );
}

export function IconCheck(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M5 12.5l4.5 4.5L19 7" />
    </Base>
  );
}

export function IconAlert(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M12 4.5l8.5 15h-17z" />
      <path d="M12 10v4M12 17h.01" />
    </Base>
  );
}

export function IconInfo(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5M12 8h.01" />
    </Base>
  );
}

export function IconLayers(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M12 3.5l8.5 4.5L12 12.5 3.5 8z" />
      <path d="M3.5 12.5L12 17l8.5-4.5" />
      <path d="M3.5 16.5L12 21l8.5-4.5" />
    </Base>
  );
}

export function IconFolder(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3.5 7a1.5 1.5 0 011.5-1.5h3.6a1.5 1.5 0 011.2.6l1 1.4H19a1.5 1.5 0 011.5 1.5v8.5A1.5 1.5 0 0119 19H5a1.5 1.5 0 01-1.5-1.5z" />
    </Base>
  );
}

export function IconDot(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" />
    </Base>
  );
}

/** Spinner: la animacion la corta `prefers-reduced-motion` desde index.css. */
export function IconSpinner(props: IconProps) {
  return (
    <Base {...props} className={`animate-spin ${props.className ?? ''}`}>
      <path d="M12 3.5a8.5 8.5 0 018.5 8.5" />
      <circle cx="12" cy="12" r="8.5" opacity="0.25" />
    </Base>
  );
}

// ---------------------------------------------------------------------------
// Iconos que los modulos piden por nombre desde su `ui.py`
// ---------------------------------------------------------------------------
export function IconActivity(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 12h3.5l2.5-6.5 4 13 2.5-6.5H21" />
    </Base>
  );
}

export function IconArchive(props: IconProps) {
  return (
    <Base {...props}>
      <rect x="3.5" y="4.5" width="17" height="4" rx="1" />
      <path d="M5 8.5V18a1.5 1.5 0 001.5 1.5h11A1.5 1.5 0 0019 18V8.5" />
      <path d="M10 12h4" />
    </Base>
  );
}

export function IconArrowRight(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M4 12h15M13 6l6 6-6 6" />
    </Base>
  );
}

export function IconBook(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M4 5.5A1.5 1.5 0 015.5 4H10a2 2 0 012 2v13a2 2 0 00-2-1.5H5.5A1.5 1.5 0 014 16z" />
      <path d="M20 5.5A1.5 1.5 0 0018.5 4H14a2 2 0 00-2 2v13a2 2 0 012-1.5h4.5A1.5 1.5 0 0020 16z" />
    </Base>
  );
}

export function IconBox(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M20.5 8.2v7.6a1.5 1.5 0 01-.78 1.32l-7 3.7a1.5 1.5 0 01-1.44 0l-7-3.7A1.5 1.5 0 013.5 15.8V8.2a1.5 1.5 0 01.78-1.32l7-3.7a1.5 1.5 0 011.44 0l7 3.7A1.5 1.5 0 0120.5 8.2z" />
      <path d="M3.8 7.4L12 11.8l8.2-4.4M12 20.5V11.8" />
    </Base>
  );
}

export function IconCalculator(props: IconProps) {
  return (
    <Base {...props}>
      <rect x="4.5" y="3" width="15" height="18" rx="2" />
      <path d="M8 7h8M8 11.5h.01M12 11.5h.01M16 11.5h.01M8 15h.01M12 15h.01M16 15v3.5M8 18.5h4" />
    </Base>
  );
}

export function IconCalendar(props: IconProps) {
  return (
    <Base {...props}>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17M8 3v4M16 3v4" />
    </Base>
  );
}

export function IconCalendarOff(props: IconProps) {
  return (
    <Base {...props}>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17M8 3v4M16 3v4M9.5 13.5l5 4M14.5 13.5l-5 4" />
    </Base>
  );
}

export function IconCheckSquare(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M20 12v6a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h9" />
      <path d="M8.5 11.5l3 3L21 5" />
    </Base>
  );
}

export function IconClipboard(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M9 4.5H7.5A1.5 1.5 0 006 6v13a1.5 1.5 0 001.5 1.5h9A1.5 1.5 0 0018 19V6a1.5 1.5 0 00-1.5-1.5H15" />
      <rect x="9" y="2.8" width="6" height="3.4" rx="1" />
      <path d="M9 11h6M9 14.5h4" />
    </Base>
  );
}

export function IconClock(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.2l3.2 2" />
    </Base>
  );
}

export function IconContact(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="12" cy="9.5" r="3.2" />
      <path d="M5.5 19.5a6.5 6.5 0 0113 0" />
      <circle cx="12" cy="12" r="9" />
    </Base>
  );
}

export function IconDatabase(props: IconProps) {
  return (
    <Base {...props}>
      <ellipse cx="12" cy="6" rx="7.5" ry="3" />
      <path d="M4.5 6v12c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3V6" />
      <path d="M4.5 12c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3" />
    </Base>
  );
}

export function IconEdit(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" />
      <path d="M17.5 3.5a2.1 2.1 0 013 3L12.5 14.5l-4 1 1-4z" />
    </Base>
  );
}

export function IconFileText(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M14 3.5H7.5A1.5 1.5 0 006 5v14a1.5 1.5 0 001.5 1.5h9A1.5 1.5 0 0018 19V7.5z" />
      <path d="M14 3.5V7a.5.5 0 00.5.5H18M9 12h6M9 15.5h6" />
    </Base>
  );
}

export function IconFlag(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M5.5 21V3.5" />
      <path d="M5.5 4.5h10l-1.6 3.2 1.6 3.3h-10z" />
    </Base>
  );
}

export function IconIdCard(props: IconProps) {
  return (
    <Base {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="11" r="2" />
      <path d="M5.5 16a3.4 3.4 0 016 0M14 10h4M14 13.5h4" />
    </Base>
  );
}

export function IconInbox(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3.5 13.5h4l1.5 2.5h6l1.5-2.5h4" />
      <path d="M6.2 4.5h11.6l2.7 9v4a2 2 0 01-2 2H5.5a2 2 0 01-2-2v-4z" />
    </Base>
  );
}

export function IconLifeBuoy(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.6" />
      <path d="M6 6l3.4 3.4M18 6l-3.4 3.4M6 18l3.4-3.4M18 18l-3.4-3.4" />
    </Base>
  );
}

export function IconLink(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M10 13.5a3.5 3.5 0 005.3.4l2.6-2.6a3.5 3.5 0 00-5-5l-1.5 1.5" />
      <path d="M14 10.5a3.5 3.5 0 00-5.3-.4l-2.6 2.6a3.5 3.5 0 005 5l1.5-1.5" />
    </Base>
  );
}

export function IconPackage(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M20.5 8.2v7.6a1.5 1.5 0 01-.78 1.32l-7 3.7a1.5 1.5 0 01-1.44 0l-7-3.7A1.5 1.5 0 013.5 15.8V8.2a1.5 1.5 0 01.78-1.32l7-3.7a1.5 1.5 0 011.44 0l7 3.7A1.5 1.5 0 0120.5 8.2z" />
      <path d="M3.8 7.4L12 11.8l8.2-4.4M12 20.5V11.8M7.8 5.1l8.2 4.4" />
    </Base>
  );
}

export function IconPriceTag(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M11 3.5H5.5A1.5 1.5 0 004 5v5.5a2 2 0 00.6 1.4l7.5 7.5a2 2 0 002.8 0l5.5-5.5a2 2 0 000-2.8L12.4 4.1a2 2 0 00-1.4-.6z" />
      <path d="M7.8 7.8h.01" />
    </Base>
  );
}

export function IconScissors(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="6.5" cy="17.5" r="2.5" />
      <path d="M8.7 8.1L20 19M20 5L8.7 15.9" />
    </Base>
  );
}

export function IconSettings(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.5l1.4 2.3 2.6-.5.6 2.6 2.4 1.1-1.1 2.4 1.1 2.4-2.4 1.1-.6 2.6-2.6-.5L12 21.5l-1.4-2.3-2.6.5-.6-2.6-2.4-1.1 1.1-2.4-1.1-2.4 2.4-1.1.6-2.6 2.6.5z" />
    </Base>
  );
}

export function IconShoppingCart(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3 4h2.2l2.3 10.5a1.5 1.5 0 001.47 1.2h7.9a1.5 1.5 0 001.47-1.2L20 7.5H6" />
      <circle cx="9.5" cy="19.5" r="1.3" />
      <circle cx="17" cy="19.5" r="1.3" />
    </Base>
  );
}

export function IconStamp(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M9 3.5h6a2 2 0 012 2c0 2-1.5 2.6-1.5 4.2v1.8h-8V9.7C7.5 8.1 6 7.5 6 5.5a2 2 0 012-2z" transform="translate(1 0)" />
      <path d="M4.5 15.5h15v2a1.5 1.5 0 01-1.5 1.5H6a1.5 1.5 0 01-1.5-1.5z" />
      <path d="M4.5 21h15" />
    </Base>
  );
}

export function IconTarget(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
    </Base>
  );
}

export function IconTrending(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3.5 16.5l5.5-5.5 3.5 3.5 7-7" />
      <path d="M14.5 7.5h5v5" />
    </Base>
  );
}

export function IconUsers(props: IconProps) {
  return (
    <Base {...props}>
      <circle cx="9.5" cy="8.5" r="3.3" />
      <path d="M3.5 19.5a6 6 0 0112 0" />
      <path d="M16 5.6a3.3 3.3 0 010 5.9M17 14.2a6 6 0 013.5 5.3" />
    </Base>
  );
}

export function IconWallet(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M3.5 7.5A2 2 0 015.5 5.5h11a2 2 0 012 2" />
      <rect x="3.5" y="7.5" width="17" height="12" rx="2" />
      <path d="M15.5 13.5h2.5" />
    </Base>
  );
}

// --- Hilo de seguimiento (`views/ChatterPanel`) ----------------------------
/** Globo de dialogo: el hilo de un registro. */
export function IconChat(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M20.5 12.5a7 7 0 01-7 7H8l-4 2.5.9-3.6a7 7 0 01-1.4-4.2v-.7a7 7 0 017-7h3a7 7 0 017 7v-.5z" />
      <path d="M8.5 11.5h7M8.5 14.5h4" />
    </Base>
  );
}

/** Campanita: seguir y dejar de seguir un registro. */
export function IconBell(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M18 9a6 6 0 10-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9z" />
      <path d="M13.7 19.5a2 2 0 01-3.4 0" />
    </Base>
  );
}

// --- Adjuntos (`views/AttachmentsPanel`) -----------------------------------
export function IconPaperclip(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M20 11.5l-8 8a5 5 0 01-7-7l8.5-8.5a3.4 3.4 0 014.8 4.8l-8.5 8.5a1.8 1.8 0 01-2.5-2.5l7.8-7.8" />
    </Base>
  );
}

export function IconDownload(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M12 3.5v11" />
      <path d="M7.5 10.5L12 15l4.5-4.5" />
      <path d="M4 17v1.5A2.5 2.5 0 006.5 21h11a2.5 2.5 0 002.5-2.5V17" />
    </Base>
  );
}

export function IconTrash(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M4.5 6.5h15" />
      <path d="M9.5 6.5V5a1.5 1.5 0 011.5-1.5h2A1.5 1.5 0 0114.5 5v1.5" />
      <path d="M6.5 6.5l.8 12A2 2 0 009.3 20.5h5.4a2 2 0 002-1.9l.8-12.1" />
      <path d="M10.5 10.5v6M13.5 10.5v6" />
    </Base>
  );
}

/**
 * Marco de foto. Se usa en dos lugares del panel de adjuntos: el encabezado de
 * la galería, y el recuadro con el que se reemplaza una miniatura que no se
 * pudo dibujar (por ejemplo en el archivo suelto, donde el servidor falso no
 * devuelve la foto de verdad).
 */
export function IconImage(props: IconProps) {
  return (
    <Base {...props}>
      <rect x="3" y="4.5" width="18" height="15" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="M4 16.5l4.5-4.5 3.5 3.5 3-2.5 5 4.5" />
    </Base>
  );
}

/** Cerrar. La cruz del visor de fotos a pantalla completa. */
export function IconX(props: IconProps) {
  return (
    <Base {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Base>
  );
}

/**
 * Nombre logico -> icono. Es el contrato con `ui.py` de cada modulo.
 *
 * Si el backend pide un nombre que no esta aca, `IconoDeMenu` cae al generico —
 * asi que agregar un nombre nuevo en un modulo obliga a agregarlo aca tambien.
 */
export const ICONOS_DE_MENU: Record<string, (props: IconProps) => JSX.Element> = {
  activity: IconActivity,
  archive: IconArchive,
  'arrow-right': IconArrowRight,
  book: IconBook,
  box: IconBox,
  building: IconBuilding,
  calculator: IconCalculator,
  calendar: IconCalendar,
  'calendar-off': IconCalendarOff,
  'check-square': IconCheckSquare,
  clipboard: IconClipboard,
  clock: IconClock,
  contact: IconContact,
  database: IconDatabase,
  edit: IconEdit,
  'file-text': IconFileText,
  flag: IconFlag,
  folder: IconFolder,
  home: IconHome,
  'id-card': IconIdCard,
  inbox: IconInbox,
  layers: IconLayers,
  'life-buoy': IconLifeBuoy,
  link: IconLink,
  package: IconPackage,
  'price-tag': IconPriceTag,
  scissors: IconScissors,
  settings: IconSettings,
  'shopping-cart': IconShoppingCart,
  stamp: IconStamp,
  target: IconTarget,
  trending: IconTrending,
  users: IconUsers,
  wallet: IconWallet,
};
